import React, { useEffect, useState } from "react";
import styled from "styled-components";
import WarningIcon from "@material-ui/icons/Warning";
import {
  filter,
  isArray,
  isEmpty,
  last,
  omit,
  omitBy,
  reduce,
  startCase,
  uniqBy,
  values,
} from "lodash";
import moment from "moment";
import { List } from "react-content-loader";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { ChevronDown, CircleX } from "@sonder/ui/source/icons";
import { ButtonSelector } from "@sonder/ui/source/styled/components/ButtonSelector";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import {
  FileInputAttachments,
  FileWrapper,
  convertToFileWrapper,
} from "@sonder/ui/source/styled/components/FileInputAttachments";
import { Image } from "@sonder/ui/source/styled/components/Image";
import { ImageSlide } from "@sonder/ui/source/styled/components/ImageSlide";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import { RadioSelector } from "@sonder/ui/source/styled/components/RadioSelector";
import { TimePicker } from "@sonder/ui/source/styled/components/TimePicker";
import { BatchJobType } from "@sonder/ui/source/styled/components/ToastMessage";
import { Button, ButtonProps } from "@sonder/ui/source/styled/fragments/Button";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { Checkbox } from "@sonder/ui/source/styled/fragments/Checkbox";
import { CheckboxWithLabel } from "@sonder/ui/source/styled/fragments/CheckboxWithLabel";
import { ImageGallery } from "@sonder/ui/source/styled/fragments/ImageGallery";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import {
  DangerousValidationException,
  ValidationException,
} from "@sonder/ui/source/styled/fragments/ValidationException";
import { FullDivider } from "@sonder/ui/source/styled/primitives/Divider";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useCreateTask } from "core/queries/CreateTask";
import { useCreateTasks } from "core/queries/CreateTasks";
import { useGetQueryParamSubject } from "core/queries/GetQueryParamSubject";
import { UnitConsumables } from "core/queries/GetUnitConsumables";
import { SearchForTaskContextsItem } from "core/queries/SearchForTaskContexts";
import {
  SearchForTaskSubjectsItem,
  SearchForTaskSubjectsItemSpace,
} from "core/queries/SearchForTaskSubjects";
import { getSearchItemId } from "core/utils/getSearchItemId";
import {
  CreateTaskFormPropsInput,
  addBatchJob,
  addFileUploadAttachment,
} from "tasks/actions";
import { AssigneePicker } from "tasks/components/AssigneePicker";
import { EditableText } from "tasks/components/EditableText";
import { PreferredTimeWindow } from "tasks/components/PreferredTimeWindow";
import { QuantityField } from "tasks/components/QuantityField";
import { StandaloneTaskRecurrenceDropdown } from "tasks/components/StandaloneTaskRecurrenceDropdown";
import { TaskConsumableInput } from "tasks/components/TaskConsumableInput";
import { TaskConsumablesSearch } from "tasks/components/TaskConsumablesSearch";
import { TaskContextSearch } from "tasks/components/TaskContextSearch";
import {
  RenderTriggerArgs,
  TaskDueByDate,
  TaskDueByDateInlineTrigger,
} from "tasks/components/TaskDueByDate";
import {
  TaskGuidedFlow,
  isTerminatingContextNode,
} from "tasks/components/TaskGuidedFlow";
import { TaskPriority } from "tasks/components/TaskPriority";
import { validRecurrenceSubjects } from "tasks/components/TaskRecurrence";
import {
  TaskSubjectBulkSearch,
  TaskSubjectBulkSearchResults,
} from "tasks/components/TaskSubjectBulkSearch";
import { TaskSubjectBulkTabs } from "tasks/components/TaskSubjectBulkTabs";
import { TaskSubjectSearch } from "tasks/components/TaskSubjectSearch";
import { TaskSubjectSelection } from "tasks/components/TaskSubjectSelection";
import { TaskTypePicker } from "tasks/components/TaskTypePicker";
import {
  convertTaskContextToNode,
  getTaskOptionByType,
  parseGraphQLId,
} from "tasks/helpers";
import {
  BulkFormTabs,
  BulkTaskSubmitCallbackMethod,
  FormState,
  MetadataState,
  MutableFeaturesMap,
  SharedTaskState,
  SingleTaskSubmitCallbackMethod,
  SlicedSerializedTaskContext,
  TaskContextFeatureMap,
  canAssign,
  communicationMethod,
  convertContextToFeatures,
  convertStateToMutationVariablesAndProvideDefaults,
  dropOffLocations,
  guestAlreadyAware,
  guestReceivedPreviousWarning,
  guestUrgency,
  initialFormState,
  initialMetadataState,
  initialSharedTaskState,
  intrastayCleaningConfigTimeSlotDisplayNames,
  isEmptyOrNil,
  isPreferredCommunicationsMethod,
  preferredCommunicationMethod,
  reasonForBan,
  reasonForEjection,
  reasonsForRelocation,
  securityOnsite,
  shouldIgnoreRequiredFeature,
  sourceOfRelocationRequest,
  splitFeaturesByMutability,
  subjectTypesConfig,
} from "tasks/hooks/useCreateTaskFormConfig";
import { UploadFileCategory, useUploadFiles } from "tasks/hooks/useUploadFiles";
import { useStore as useTasksStore } from "tasks/store";
import { getIntrastayCleaningConfig } from "tasks/utils/getIntrastayCleaningConfig";
import {
  CleaningWindow,
  FilesAttachable,
  FilterDropOffLocation,
  Format,
  GuestAlreadyAware,
  GuestReceivedPreviousWarning,
  GuestUrgency,
  Host,
  ReasonForBan,
  ReasonForEjection,
  ReasonForRelocation,
  RequestedCleaningTimeSlot,
  Resource,
  SecurityOnsite,
  SourceOfRelocationRequest,
  SubjectTypeEnum,
  TaskContextSubjectTypeEnum,
  TypeEnum,
} from "typings/globalGQLTypes";

const StyledInputGroup = styled(InputGroup)`
  align-items: flex-start;
  flex-wrap: wrap;
`;

const StyledAssigneeWrapper = styled.span`
  cursor: pointer;
  display: inline-flex;
`;

const StyledEditableTextWrapper = styled.div`
  background-color: ${colors.white};
  border: ${borders.standard};
  border-radius: 0.3rem;
  padding: 0.5rem;
`;

const TaskDueByDateInlineTriggerWrapper = styled.span`
  align-items: center;
  display: inline-flex;

  ${CancelButton} {
    margin-left: 0.5rem;
  }
`;

const StyledSectionHeader = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const StyledPreviewComponent = styled.div`
  background-color: ${colors.white};
  border: ${borders.standard};
  border-radius: 0.3rem;
  color: ${colors.gray6};
  margin-bottom: 1rem;
  padding: 1rem 1rem 0 1rem;
`;

const StyledPreviewTitle = styled.span`
  color: ${colors.black};

  & + span {
    &:before {
      content: " | ";
    }
  }
`;

const BulkBanner = styled.div`
  background-color: ${colors.yellow};
  padding: 0.625rem 0;
  font-weight: bold;
  text-align: center;
  width: 100%;
`;

const BlockInputField = styled(InputField)`
  display: flex;
  flex: 0 1 auto;
`;

const BulkSubjectCreationInfoPanel = styled.div`
  align-items: center;
  background: ${colors.blueE};
  border: 1px solid ${colors.blue7};
  border-radius: 0.3rem;
  color: ${colors.black};
  display: flex;
  margin-bottom: 1rem;
  padding: 0.5rem;

  svg {
    color: ${colors.blue7};
  }

  svg + span {
    margin-left: 0.5rem;
  }
`;

const DateTimePickerContainer = styled.div`
  display: flex;
`;

const StyledValidationException = styled(ValidationException)`
  margin-bottom: 1rem;
`;

const StyledDangerousValidationException = styled(DangerousValidationException)`
  margin-bottom: 1rem;
`;

interface CustomDateInlineTriggerArgs extends RenderTriggerArgs, ButtonProps {}

const CustomDateInlineTrigger = (props: CustomDateInlineTriggerArgs) => {
  const { date, onClick } = props;
  return (
    <TaskDueByDateInlineTriggerWrapper>
      <Button iconRight onClick={onClick}>
        {date && moment(date).format("ddd MMM D - h:mm A")}
        {!date && "Select Date..."}
        <ChevronDown />
      </Button>
    </TaskDueByDateInlineTriggerWrapper>
  );
};

const CustomTaskDueByDateInlineTrigger = (props: RenderTriggerArgs) => {
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  const { date } = props;
  return (
    <TaskDueByDateInlineTriggerWrapper
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TaskDueByDateInlineTrigger {...props} />
      {hovered && date && (
        <CancelButton
          onClick={e => {
            if (props.onClear) props.onClear();
            e.stopPropagation();
          }}
        >
          <CircleX />
        </CancelButton>
      )}
    </TaskDueByDateInlineTriggerWrapper>
  );
};

const StyledCheckboxWithLabel = styled(CheckboxWithLabel)`
  cursor: pointer;

  label {
    cursor: pointer;
  }
`;

interface CheckboxSelectorProps {
  active?: boolean;
  label?: string;
  onChange?: (active: boolean) => void;
}

const CheckboxSelector = ({
  active,
  label,
  onChange,
}: CheckboxSelectorProps) => {
  return (
    <StyledCheckboxWithLabel
      onClick={() => {
        if (onChange) onChange(!active);
      }}
    >
      <Checkbox checked={active} name={label} />
      {label && <Label>{label}</Label>}
    </StyledCheckboxWithLabel>
  );
};

const EVENT_NAME = "create_task_modal";
const TASK_ID = "NEW_TASK";
const OTHER_CONTEXT_NAME = "Other";

const isOtherContext = (node?: SlicedSerializedTaskContext | null) => {
  return node?.contextItemName === OTHER_CONTEXT_NAME;
};

const getSubjectId = (taskSubject: SearchForTaskSubjectsItem | null) => {
  if (!taskSubject) return null;
  const { resource } = taskSubject;
  switch (resource.__typename) {
    case TaskContextSubjectTypeEnum.Building:
      return resource.buildingId;
    // Unfortunate, TaskContextSubjectTypeEnum.Cuf_Space is `Cuf_Space`, but the __typename is "CufSpace" :O
    case "CufSpace":
    case TaskContextSubjectTypeEnum.Inquiry:
    case TaskContextSubjectTypeEnum.LeaseInquiry:
    case TaskContextSubjectTypeEnum.Unit:
      return resource.id;

    default:
      return null;
  }
};

const getSubjectCityId = (taskSubject: SearchForTaskSubjectsItem | null) => {
  if (!taskSubject) return null;
  const { resource } = taskSubject;
  switch (resource.__typename) {
    // Unfortunate, TaskContextSubjectTypeEnum.Cuf_Space is `Cuf_Space`, but the __typename is "CufSpace" :O
    case "CufSpace":
      return resource.building.city.id;
    case TaskContextSubjectTypeEnum.Inquiry:
    case TaskContextSubjectTypeEnum.LeaseInquiry:
      return resource.unit?.city.id;
    case TaskContextSubjectTypeEnum.Unit:
    case TaskContextSubjectTypeEnum.Building:
      return resource.city?.id;

    default:
      return null;
  }
};

const getSubjectPreviewText = (
  taskSubject: SearchForTaskSubjectsItem | null,
) => {
  if (!taskSubject)
    return {
      title: "",
      subtitle: "",
    };

  const { resource } = taskSubject;

  switch (resource.__typename) {
    // Unfortunate, TaskContextSubjectTypeEnum.Cuf_Space is `Cuf_Space`, but the __typename is "CufSpace" :O
    case "CufSpace":
      return {
        title: taskSubject.title,
        subtitle: startCase(
          (taskSubject.resource as SearchForTaskSubjectsItemSpace).type,
        ),
      };

    case TaskContextSubjectTypeEnum.Unit:
      return {
        title: taskSubject.title,
        subtitle: "",
      };

    case TaskContextSubjectTypeEnum.Inquiry:
    case TaskContextSubjectTypeEnum.LeaseInquiry:
      return {
        title: taskSubject.title,
        subtitle: [
          resource.unit.shortInternalRef,
          resource.checkInTime && resource.checkOutTime
            ? `${moment(resource.checkInTime).format(
                "MMM Do @ hh:mm A",
              )} - ${moment(resource.checkOutTime).format("MMM Do @ hh:mm A")}`
            : null,
        ]
          .filter(x => Boolean(x))
          .join(" | "),
      };

    case TaskContextSubjectTypeEnum.Building:
      return {
        title: taskSubject.title,
        subtitle: [
          resource.fullAddress,
          `Total Live Units: ${resource.liveUnitCount}`,
        ]
          .filter(Boolean)
          .join(" | "),
      };

    default:
      return {
        title: "",
        subtitle: "",
      };
  }
};

const getSubjectSearchFilterInputType = (subjectType?: string) => {
  switch (subjectType) {
    case "CufSpace":
      return TaskContextSubjectTypeEnum.Cuf_Space;
    case "TomBooking":
      return TaskContextSubjectTypeEnum.Tom_Booking;
    default:
      return subjectType;
  }
};

export interface useCreateTaskFormProps extends CreateTaskFormPropsInput {}

export const useCreateTaskForm = ({
  subjectId: initialSubjectId,
  subjectType: initialSubjectType,
  taskType: initialTaskType,
}: useCreateTaskFormProps = {}) => {
  const {
    context: prefilledContext,
    error: prefilledError,
    loading: prefilledLoading,
    subject: prefilledSubject,
    subjectId: prefilledSubjectId,
    subjectType: prefilledSubjectType,
    taskType: prefilledTaskType,
  } = useGetQueryParamSubject({
    subjectId: initialSubjectId,
    subjectType: initialSubjectType,
    taskType: initialTaskType,
  });
  const getInitialOrPrefilledSubjectType = () =>
    (prefilledSubjectType && subjectTypesConfig[prefilledSubjectType]) ||
    initialFormState.subjectType;
  const getInitialOrPrefilledTaskType = () => {
    const { validTaskTypes } = getInitialOrPrefilledSubjectType();
    const newTaskTypeIndex = prefilledTaskType
      ? validTaskTypes.indexOf(prefilledTaskType)
      : -1;
    return newTaskTypeIndex !== -1
      ? validTaskTypes[newTaskTypeIndex]
      : validTaskTypes[0];
  };
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const { state: authState } = useAuthStore();
  const { dispatch } = useTasksStore();
  const [sharedTaskState, setSharedTaskState] = useState<SharedTaskState>({
    ...initialSharedTaskState,
    type: getInitialOrPrefilledTaskType(),
  });
  const [formState, setFormState] = useState<FormState>({
    ...initialFormState,
    subjectType: getInitialOrPrefilledSubjectType(),
  });
  // Metadata will change depending on what type of task is selected.
  const [metadataState, setMetadataState] =
    useState<MetadataState>(initialMetadataState);
  // TaskContext State can be decided via Search or Guided Flow
  const [taskContextState, setTaskContextState] =
    useState<SlicedSerializedTaskContext | null>(null);
  // Files will be uploaded after Task Creation in Single Task Creation Flow (for now, to make sure we don't break this)
  // In Bulk Task Creation, we will upload first (allowing deleting via Mutation), and then attach the results.
  const [files, setFiles] = useState<FileWrapper[]>([]);
  const [bulkFiles, setBulkFiles] = useState<FileWrapper[]>([]);
  // This will solely be for Bulk Task Creation File Uploads for now.
  const {
    onUploadFiles,
    // uploadState: { loading: isUploading, data: uploadData, error: uploadError },
  } = useUploadFiles();

  const onChangeSearchTaskContext = (
    selectedContext: SearchForTaskContextsItem | null,
  ) => {
    const node = selectedContext
      ? convertTaskContextToNode(selectedContext)
      : null;
    setSharedTaskState({
      ...sharedTaskState,
      ...omit(initialSharedTaskState, ["subjectId", "type"]),
      name: (!isOtherContext(node) && node?.contextName) || "",
      tomTaskContextId: node?.contextId ? Number(node?.contextId) : null,
    });
    setMetadataState(initialMetadataState);
    setFormState({
      ...formState,
      searchedContext: selectedContext,
    });
    setTaskContextState(node);
  };

  const cityId = getSubjectCityId(formState.subject) || authState.user?.cityId;

  // If the prefilledSubjectId exists, set it into the form once.
  // Populate FormState with Prefilled Subject on GQL Load
  const isLoadingPrefilledSubject = prefilledSubjectId && prefilledLoading;
  useEffect(() => {
    if (prefilledSubject?.id && !sharedTaskState.subjectId) {
      setSharedTaskState({
        ...sharedTaskState,
        subjectId: getSearchItemId(prefilledSubject),
      });
      setFormState({
        ...formState,
        subject: prefilledSubject,
        subjectType: getInitialOrPrefilledSubjectType(),
      });
    }
    if (prefilledContext?.id && !sharedTaskState.tomTaskContextId)
      onChangeSearchTaskContext(prefilledContext);
  }, [
    prefilledLoading,
    prefilledError,
    prefilledSubjectId,
    prefilledSubject?.id,
    prefilledContext?.id,
  ]);

  const onReset = () => {
    setSharedTaskState(initialSharedTaskState);
    setMetadataState(initialMetadataState);
    setFormState(initialFormState);
    setFiles([]);
    setBulkFiles([]);
  };

  const isOther = isOtherContext(taskContextState);

  // Features define what DOM elements are shown and what is passed to metadata.
  // A mutable contextFeature can and should be changed, an immutable contextFeature passes it's defaultValue to the BE.
  const { mutableContextFeatures } = taskContextState
    ? splitFeaturesByMutability(convertContextToFeatures(taskContextState))
    : ({
        mutableContextFeatures: {},
        immutableContextFeatures: {},
      } as MutableFeaturesMap);

  const defaultValues = reduce(
    mutableContextFeatures,
    (memo, acc, index) => {
      return { ...memo, [index]: acc.defaultValue };
    },
    {},
  );

  const getFromMetadataState = (key: string) =>
    (key in metadataState
      ? metadataState[key as keyof MetadataState]
      : undefined) ||
    (key in mutableContextFeatures
      ? mutableContextFeatures[key as keyof TaskContextFeatureMap]?.defaultValue
      : undefined) ||
    undefined;

  // TODO: Validation needs to be properly handled.
  const [onCreateTask, { loading: isCreating, error: createError }] =
    useCreateTask({
      refetchQueries: ["GetTasks"],
      awaitRefetchQueries: true,
    });

  const [onCreateTasks, { loading: isBulkCreating, error: bulkCreateError }] =
    useCreateTasks({
      refetchQueries: ["GetTasks"],
      awaitRefetchQueries: true,
    });

  const shouldShowPreferredCommunicationsMethod =
    mutableContextFeatures.preferredCommunicationMethod &&
    sharedTaskState.communicationMethod &&
    !isPreferredCommunicationsMethod(sharedTaskState.communicationMethod);

  const isCommunicationsMethodInvalidAndPreferredCommunicationsMethodEmpty =
    mutableContextFeatures.preferredCommunicationMethod &&
    (!sharedTaskState.communicationMethod ||
      !isPreferredCommunicationsMethod(sharedTaskState.communicationMethod)) &&
    !sharedTaskState.preferredCommunicationMethod;

  const emptyRequiredMutableContextFeatures = filter(
    mutableContextFeatures,
    (feature, index) =>
      feature.required &&
      !feature.defaultValue &&
      !getFromMetadataState(index) &&
      shouldIgnoreRequiredFeature(index),
  );

  const areRequiredMutableContextFeaturesEmpty =
    emptyRequiredMutableContextFeatures.length > 0;

  // For Single / Bulk Creation
  const isSingleSubject =
    formState.bulkFormState?.selectedTab === BulkFormTabs.Single ||
    !formState.bulkFormState;
  const isBulkSubject =
    formState.bulkFormState?.selectedTab === BulkFormTabs.Multiple;
  const hasValidSubject =
    (isSingleSubject && sharedTaskState.subjectId) ||
    (isBulkSubject && (formState.bulkFormState?.subjects || []).length > 0);

  const isCreateDisabled =
    isCreating ||
    isBulkCreating ||
    (isOther && !sharedTaskState.name) ||
    !sharedTaskState.tomTaskContextId ||
    formState.isPreferredWindowBelowThreshold ||
    isCommunicationsMethodInvalidAndPreferredCommunicationsMethodEmpty ||
    areRequiredMutableContextFeaturesEmpty;

  const generalizedError = createError || bulkCreateError;

  const subjectPreviewText = getSubjectPreviewText(formState.subject);
  const bulkSubjectPreviewText =
    formState.bulkFormState?.parentSubject &&
    getSubjectPreviewText(formState.bulkFormState?.parentSubject);

  const onAddFiles = async (addedFiles: FileWrapper[]) => {
    try {
      // Get the files, convert to correct TS format, omit previously uploaded files.
      const uploadingFiles = (addedFiles || [])
        .filter(f => !f.error && !f.uploadedFile && !f.uploading)
        .map(f => ({
          ...f,
          uploading: true,
          error: null,
        }));
      const dedupedBulkFiles = uniqBy(
        [...bulkFiles, ...uploadingFiles],
        file => file.id,
      );
      // Store in state to show "uploading".
      setBulkFiles(dedupedBulkFiles);
      // Actually upload files
      const { data } = await onUploadFiles({
        category: UploadFileCategory.TaskAttachment,
        files: uploadingFiles.map(f => f.file),
      });
      // Convert Uploaded Files / Errors into Appropriate Format
      const dedupedFinalFiles = uniqBy(
        [
          ...bulkFiles,
          ...(data?.uploadedFiles.map(f =>
            convertToFileWrapper(f.originalFile, { uploadedFile: f }),
          ) || []),
          ...(data?.erroredFiles.map(f =>
            convertToFileWrapper(f.originalFile, { error: f.error }),
          ) || []),
        ],
        file => file.id,
      );
      // Store Uploaded Files / Errors into State.
      setBulkFiles(dedupedFinalFiles);
      // Track successful upload
      track({
        eventName: EVENT_NAME,
        action: "success",
        element: `${EVENT_NAME}.photo_upload`,
        props: {
          fileNames: addedFiles.map(f => f.file.name),
        },
      });
    } catch (error) {
      errorReporter.notify(error);
      track({
        eventName: EVENT_NAME,
        action: "failure",
        element: `${EVENT_NAME}.photo_upload`,
        props: {
          fileNames: addedFiles.map(f => f.file.name),
        },
      });
    }
  };

  const onDeleteFile = async (deletedFile: FileWrapper) => {
    setBulkFiles(bulkFiles.filter(f => f.id !== deletedFile.id));
  };

  const onSubmitSingleTask = async (
    callback: SingleTaskSubmitCallbackMethod,
  ) => {
    // Convert it into a format the BE can handle.
    const createTaskMutationVariables =
      convertStateToMutationVariablesAndProvideDefaults(sharedTaskState, {
        ...defaultValues,
        ...metadataState,
      });
    try {
      const result = await onCreateTask({
        variables: {
          ...createTaskMutationVariables,
        },
      });
      const newTaskId =
        result.data?.createTask?.task?.id &&
        parseGraphQLId(result.data?.createTask?.task?.id);
      if (newTaskId && files.length)
        dispatch(
          addFileUploadAttachment({
            attachableId: newTaskId,
            attachableType: FilesAttachable.task,
            files: [...files.map(f => f.file)].reverse(),
          }),
        );

      track({
        eventName: EVENT_NAME,
        action: "click",
        element: "create_task_modal_submit",
        props: {
          taskId: newTaskId,
          subjectId: createTaskMutationVariables.subjectId,
          type: createTaskMutationVariables.type,
        },
      });
      callback(newTaskId);
    } catch (e) {
      errorReporter.notify(e);
    }
  };

  const onSubmitBulkTask = async (callback: BulkTaskSubmitCallbackMethod) => {
    // Convert it into a format the BE can handle.
    const createTaskMutationVariables =
      convertStateToMutationVariablesAndProvideDefaults(sharedTaskState, {
        ...defaultValues,
        ...metadataState,
      });
    try {
      const subjectIds = (formState.bulkFormState?.subjects || [])
        .map(s => getSubjectId(s) || "")
        .filter(x => !!x);
      const result = await onCreateTasks({
        variables: {
          ...createTaskMutationVariables,
          attachments: bulkFiles
            .filter(f => f.uploadedFile && !f.error && !f.uploading)
            .map(f => ({
              host: Host.cloudinary,
              format: f.uploadedFile?.format as Format,
              resourceType: f.uploadedFile?.resourceType as Resource,
              publicId: f.uploadedFile?.publicId,
            })),
          subjectIds,
          subjectType: formState.subjectType
            .subjectType as unknown as SubjectTypeEnum,
          type: createTaskMutationVariables.type as TypeEnum,
        },
      });
      const batchJobId = result.data?.batchCreateTasks?.batchJob?.id;
      if (batchJobId) {
        track({
          eventName: EVENT_NAME,
          action: "click",
          element: "create_bulk_tasks_modal_submit",
          props: {
            taskId: batchJobId,
            subjectIds,
            subjectType: formState.subjectType
              .subjectType as unknown as SubjectTypeEnum,
            type: createTaskMutationVariables.type as TypeEnum,
          },
        });
        dispatch(addBatchJob(batchJobId, BatchJobType.Create));
        callback(batchJobId);
      }
    } catch (e) {
      errorReporter.notify(e);
    }
  };

  const onSubmit = async (
    callback: SingleTaskSubmitCallbackMethod | BulkTaskSubmitCallbackMethod,
  ) => {
    if (isSingleSubject) await onSubmitSingleTask(callback);
    if (isBulkSubject) await onSubmitBulkTask(callback);
  };

  // One Offs
  const removeConsumable = (consumable: UnitConsumables) => {
    setFormState({
      ...formState,
      consumables: filter(
        formState.consumables,
        item => item?.metadataKey !== consumable?.metadataKey,
      ),
    });

    setMetadataState(
      omitBy(
        { ...metadataState, [consumable?.metadataKey]: undefined },
        isEmptyOrNil,
      ),
    );
  };

  const intrastayCleaningConfigAvailableTimes =
    getIntrastayCleaningConfig(formState.subject)?.availableTimes || [];

  const metadataSourceOfRelocationRequest = getFromMetadataState(
    "sourceOfRelocationRequest",
  );

  const Error = generalizedError ? (
    <StyledDangerousValidationException message={generalizedError.message} />
  ) : null;

  const CreateFields = {
    Title: "Create Task",
    Error,
    Header: (
      <>
        <TaskSubjectSelection
          disabled={isLoadingPrefilledSubject}
          taskSubjects={values(subjectTypesConfig)}
          value={formState.subjectType}
          onChange={subjectType => {
            if (subjectType !== formState.subjectType) {
              setSharedTaskState({
                ...initialSharedTaskState,
                type: subjectType.validTaskTypes[0],
              });
              setMetadataState(initialMetadataState);
              setFormState({
                ...initialFormState,
                subjectType,
              });
            }
          }}
        />
        <TaskSubjectBulkTabs
          disabled={isLoadingPrefilledSubject}
          selectedTab={formState.bulkFormState?.selectedTab}
          subjectType={formState.subjectType}
          onChange={props => {
            const { tab, parentSubjectType } = props;
            const parentSubjectTypeConfig =
              parentSubjectType && subjectTypesConfig[parentSubjectType];
            if (tab !== formState.bulkFormState?.selectedTab) {
              setMetadataState(initialMetadataState);
              setFormState({
                ...formState,
                isGuidedFlowOpen: initialFormState.isGuidedFlowOpen,
                searchedContext: initialFormState.searchedContext,
                bulkFormState: {
                  ...formState.bulkFormState,
                  parentSubject: null,
                  parentSubjectType: parentSubjectTypeConfig || null,
                  subjects: [],
                  selectedTab: tab,
                },
              });
            }
          }}
        />
        {formState.subjectType.subjectType ===
          TaskContextSubjectTypeEnum.Unit &&
          !isBulkSubject && (
            <BulkBanner>
              Check out Bulk Task Creation - Select &#39;Multiple Units&#39; to
              get started!
            </BulkBanner>
          )}
      </>
    ),
    Content: (
      <>
        <BlockInputField>
          {isSingleSubject && (
            <TaskSubjectSearch
              subject={formState.subject}
              subjectType={formState.subjectType}
              onChange={subject => {
                const subjectId = subject ? getSearchItemId(subject) : null;
                const subjectType =
                  getSubjectSearchFilterInputType(
                    subject?.resource.__typename,
                  ) || formState.subjectType.subjectType;
                if (subjectId !== sharedTaskState.subjectId) {
                  setSharedTaskState({
                    ...sharedTaskState,
                    ...initialSharedTaskState,
                    type: formState.subjectType.validTaskTypes[0],
                    subjectId,
                  });
                  setMetadataState(initialMetadataState);
                  setFormState({
                    ...formState,
                    isGuidedFlowOpen: initialFormState.isGuidedFlowOpen,
                    searchedContext: initialFormState.searchedContext,
                    subject,
                    subjectType: {
                      ...formState.subjectType,
                      subjectType: subjectType as TaskContextSubjectTypeEnum,
                    },
                  });
                }
              }}
            />
          )}
          {isBulkSubject && formState.bulkFormState?.parentSubjectType && (
            <>
              <Label>
                {formState.bulkFormState?.parentSubjectType.displayName}
              </Label>
              <TaskSubjectSearch
                subject={formState.bulkFormState?.parentSubject || null}
                subjectType={formState.bulkFormState?.parentSubjectType}
                onChange={subject => {
                  setMetadataState(initialMetadataState);
                  setFormState({
                    ...formState,
                    isGuidedFlowOpen: initialFormState.isGuidedFlowOpen,
                    searchedContext: initialFormState.searchedContext,
                    subject: formState.subject,
                    bulkFormState: {
                      ...formState.bulkFormState,
                      parentSubject: subject,
                      parentSubjectType:
                        formState.bulkFormState?.parentSubjectType || null,
                      selectedTab:
                        formState.bulkFormState?.selectedTab ||
                        BulkFormTabs.Single,
                      subjects: [],
                    },
                  });
                }}
              />
            </>
          )}
        </BlockInputField>
        {isBulkSubject && formState.bulkFormState?.parentSubjectType && (
          <BlockInputField>
            <Label>
              {formState.subjectType.bulkTaskConfig?.searchTitle ||
                "Select Subject(s)"}
            </Label>
            <TaskSubjectBulkSearch
              disabled={!formState.bulkFormState?.parentSubject}
              parentSubject={formState.bulkFormState?.parentSubject}
              subjectType={formState.subjectType}
              subjects={formState.bulkFormState?.subjects || []}
              onChange={newSubjects => {
                setFormState({
                  ...formState,
                  isGuidedFlowOpen: initialFormState.isGuidedFlowOpen,
                  searchedContext: initialFormState.searchedContext,
                  bulkFormState: {
                    ...formState.bulkFormState,
                    parentSubject:
                      formState.bulkFormState?.parentSubject || null,
                    parentSubjectType:
                      formState.bulkFormState?.parentSubjectType || null,
                    selectedTab:
                      formState.bulkFormState?.selectedTab ||
                      BulkFormTabs.Single,
                    subjects: newSubjects,
                  },
                });
              }}
            />
          </BlockInputField>
        )}
        <BlockInputField>
          <Label>Task Type</Label>
          <TaskTypePicker
            disabled={formState.subjectType.validTaskTypes.length === 0}
            subjectType={formState.subjectType}
            value={sharedTaskState.type}
            onChange={taskType => {
              setSharedTaskState({
                ...sharedTaskState,
                tomTaskContextId: initialSharedTaskState.tomTaskContextId,
                type: taskType || initialSharedTaskState.type,
              });
              setMetadataState(initialMetadataState);
              setFormState({
                ...formState,
                guidedFlowState: initialFormState.guidedFlowState,
                isGuidedFlowOpen: initialFormState.isGuidedFlowOpen,
                searchedContext: initialFormState.searchedContext,
              });
            }}
          />
        </BlockInputField>
        {sharedTaskState.type && hasValidSubject && (
          <>
            {!formState.isGuidedFlowOpen && (
              <BlockInputField>
                <Label>Context*</Label>
                <TaskContextSearch
                  locationPlaces={formState.subjectType.locationPlaces}
                  selectedContext={formState.searchedContext}
                  subjectType={formState.subjectType.subjectType}
                  taskType={sharedTaskState.type}
                  onChange={onChangeSearchTaskContext}
                  onGuidedFlowOpen={() =>
                    setFormState({
                      ...formState,
                      isGuidedFlowOpen: true,
                    })
                  }
                />
              </BlockInputField>
            )}
            {formState.isGuidedFlowOpen && (
              <BlockInputField>
                <Label>Context*</Label>
                <TaskGuidedFlow
                  guidedFlowState={formState.guidedFlowState}
                  subjectType={formState.subjectType.subjectType}
                  taskType={sharedTaskState.type}
                  onGuidedFlowChange={newGuidedFlowState => {
                    setFormState({
                      ...formState,
                      guidedFlowState: newGuidedFlowState,
                    });
                    const { contextNodes } = newGuidedFlowState;
                    const lastNewContextNode = last(contextNodes);
                    const terminatingContextNode = isTerminatingContextNode(
                      lastNewContextNode,
                    )
                      ? lastNewContextNode
                      : null;
                    setSharedTaskState({
                      ...sharedTaskState,
                      ...omit(initialSharedTaskState, ["subjectId", "type"]),
                      name:
                        (!isOtherContext(terminatingContextNode) &&
                          terminatingContextNode?.contextName) ||
                        "",
                      tomTaskContextId: terminatingContextNode?.contextId
                        ? Number(terminatingContextNode?.contextId)
                        : null,
                    });
                    setMetadataState(initialMetadataState);
                    setTaskContextState(terminatingContextNode || null);
                  }}
                  onGuidedFlowClose={() => {
                    setFormState({
                      ...formState,
                      guidedFlowState: initialFormState.guidedFlowState,
                      isGuidedFlowOpen: false,
                    });
                    setSharedTaskState({
                      ...sharedTaskState,
                      tomTaskContextId: null,
                    });
                  }}
                />
              </BlockInputField>
            )}
          </>
        )}
        {sharedTaskState.tomTaskContextId && (
          <>
            {isOther && (
              <BlockInputField>
                <Label>Name*</Label>
                <Input
                  placeholder="Enter a name"
                  type="text"
                  value={sharedTaskState.name}
                  onChange={e =>
                    setSharedTaskState({
                      ...sharedTaskState,
                      name: e.currentTarget.value || "",
                    })
                  }
                />
              </BlockInputField>
            )}
            {/* TODO: LOOK AT SOME STANDARDIZATION ON LABELS / ETC AFTER WE FILL THIS UP :) */}
            {!isEmpty(mutableContextFeatures) && (
              <>
                {mutableContextFeatures.consumable && (
                  <>
                    <BlockInputField>
                      <Label>
                        {mutableContextFeatures.consumable.name}
                        {mutableContextFeatures.consumable.required ? "*" : ""}
                      </Label>
                      <TaskConsumablesSearch
                        subject={formState.subject}
                        subjectId={sharedTaskState?.subjectId}
                        onAddConsumable={consumableItem =>
                          setFormState({
                            ...formState,
                            consumables: [
                              ...formState.consumables,
                              ...consumableItem,
                            ],
                          })
                        }
                      />
                    </BlockInputField>
                    {formState.consumables.length
                      ? formState.consumables?.map(consumable => (
                          <TaskConsumableInput
                            consumable={consumable}
                            key={consumable?.metadataKey}
                            value={
                              metadataState[
                                consumable?.metadataKey as keyof MetadataState
                              ] || ""
                            }
                            onChange={value => {
                              setMetadataState({
                                ...metadataState,
                                [consumable?.metadataKey]: parseInt(value, 10),
                              });
                            }}
                            onRemove={item => removeConsumable(item)}
                          />
                        ))
                      : null}
                  </>
                )}
                {mutableContextFeatures.sopExceptionNotes && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.sopExceptionNotes.name}
                      {mutableContextFeatures.sopExceptionNotes.required
                        ? "*"
                        : ""}
                    </Label>
                    <StyledEditableTextWrapper>
                      <EditableText
                        placeholder="If making an exception to the max quantities, please explain below."
                        value={getFromMetadataState("sopExceptionNotes")}
                        onChange={notes =>
                          setMetadataState({
                            ...metadataState,
                            sopExceptionNotes: notes,
                          })
                        }
                      />
                    </StyledEditableTextWrapper>
                  </BlockInputField>
                )}
                <StyledInputGroup>
                  {mutableContextFeatures.quantity && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.quantity.name}
                        {mutableContextFeatures.quantity.required ? "*" : ""}
                      </Label>
                      <QuantityField
                        min={1}
                        placeholder="Enter Quantity"
                        type="number"
                        value={getFromMetadataState("quantity")}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setMetadataState({
                            ...metadataState,
                            quantity: parseInt(e.currentTarget.value, 10),
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.preferredTimeWindow && (
                    <InputField>
                      <Label title="Minimum 2 hours time window ">
                        Preferred Time Window
                        {mutableContextFeatures.preferredTimeWindow.required
                          ? "*"
                          : ""}
                      </Label>
                      <PreferredTimeWindow
                        onChange={(date: string, isBelowThreshold: boolean) => {
                          setMetadataState({
                            ...metadataState,
                            preferredTimeWindow: date,
                          });
                          setFormState({
                            ...formState,
                            isPreferredWindowBelowThreshold: isBelowThreshold,
                          });
                        }}
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.followedByGuestServices && (
                    <InputField>
                      <CheckboxSelector
                        active={
                          getFromMetadataState("followedByGuestServices") ===
                          "yes"
                        }
                        label={`${
                          mutableContextFeatures.followedByGuestServices.name
                        }${
                          mutableContextFeatures.followedByGuestServices
                            .required
                            ? "*"
                            : ""
                        }`}
                        onChange={active => {
                          setMetadataState({
                            ...metadataState,
                            followedByGuestServices: active ? "yes" : "no",
                          });
                        }}
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.ejectionAt && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.ejectionAt.name}
                        {mutableContextFeatures.ejectionAt.required ? "*" : ""}
                      </Label>
                      <TaskDueByDate
                        isTurnoverDisabled
                        alignContent="left"
                        bookingInfo={null}
                        date={
                          getFromMetadataState("ejectionAt")
                            ? moment(getFromMetadataState("ejectionAt"))
                            : null
                        }
                        eventName={EVENT_NAME}
                        futureCheckouts={[]}
                        recurrenceSetting={null}
                        renderTrigger={props => (
                          <CustomDateInlineTrigger
                            {...props}
                            date={getFromMetadataState("ejectionAt") || null}
                          />
                        )}
                        taskId={TASK_ID}
                        onChange={variables =>
                          setMetadataState({
                            ...metadataState,
                            ejectionAt: variables?.dueBy || null,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.reasonForEjection && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.reasonForEjection.name}
                        {mutableContextFeatures.reasonForEjection.required
                          ? "*"
                          : ""}
                      </Label>
                      <RadioSelector
                        label="Select Reason..."
                        options={reasonForEjection}
                        required={
                          mutableContextFeatures.reasonForEjection.required
                        }
                        selected={getFromMetadataState("reasonForEjection")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            reasonForEjection: value as ReasonForEjection,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.reasonForBan && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.reasonForBan.name}
                        {mutableContextFeatures.reasonForBan.required
                          ? "*"
                          : ""}
                      </Label>
                      <RadioSelector
                        label="Select Reason..."
                        options={reasonForBan}
                        required={mutableContextFeatures.reasonForBan.required}
                        selected={getFromMetadataState("reasonForBan")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            reasonForBan: value as ReasonForBan,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.guestAlreadyAware && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.guestAlreadyAware.name}
                        {mutableContextFeatures.guestAlreadyAware.required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={guestAlreadyAware}
                        required={
                          mutableContextFeatures.guestAlreadyAware.required
                        }
                        selected={getFromMetadataState("guestAlreadyAware")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            guestAlreadyAware: value as GuestAlreadyAware,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.guestReceivedPreviousWarning && (
                    <InputField>
                      <Label>
                        {
                          mutableContextFeatures.guestReceivedPreviousWarning
                            .name
                        }
                        {mutableContextFeatures.guestReceivedPreviousWarning
                          .required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={guestReceivedPreviousWarning}
                        required={
                          mutableContextFeatures.guestReceivedPreviousWarning
                            .required
                        }
                        selected={getFromMetadataState(
                          "guestReceivedPreviousWarning",
                        )}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            guestReceivedPreviousWarning:
                              value as GuestReceivedPreviousWarning,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.securityOnsite && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.securityOnsite.name}
                        {mutableContextFeatures.securityOnsite.required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={securityOnsite}
                        required={
                          mutableContextFeatures.securityOnsite.required
                        }
                        selected={getFromMetadataState("securityOnsite")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            securityOnsite: value as SecurityOnsite,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.desiredCheckInTime && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.desiredCheckInTime.name}
                        {mutableContextFeatures.desiredCheckInTime.required
                          ? "*"
                          : ""}
                      </Label>
                      <TimePicker
                        date={
                          getFromMetadataState("desiredCheckInTime")
                            ? moment(
                                getFromMetadataState("desiredCheckInTime"),
                                "H:mm",
                              )
                            : undefined
                        }
                        onChange={nextTime =>
                          setMetadataState({
                            ...metadataState,
                            desiredCheckInTime: nextTime.format("HH:mm"),
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.desiredCheckOutTime && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.desiredCheckOutTime.name}
                        {mutableContextFeatures.desiredCheckOutTime.required
                          ? "*"
                          : ""}
                      </Label>
                      <TimePicker
                        date={
                          getFromMetadataState("desiredCheckOutTime")
                            ? moment(
                                getFromMetadataState("desiredCheckOutTime"),
                                "H:mm",
                              )
                            : undefined
                        }
                        onChange={nextTime =>
                          setMetadataState({
                            ...metadataState,
                            desiredCheckOutTime: nextTime.format("HH:mm"),
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.communicationMethod && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.communicationMethod.name}
                        {mutableContextFeatures.communicationMethod.required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={communicationMethod}
                        required={
                          mutableContextFeatures.communicationMethod.required
                        }
                        selected={sharedTaskState.communicationMethod}
                        onChange={value => {
                          const stringValue = value
                            ? value.toString()
                            : undefined;
                          setSharedTaskState({
                            ...sharedTaskState,
                            communicationMethod: stringValue,
                            preferredCommunicationMethod:
                              stringValue &&
                              isPreferredCommunicationsMethod(stringValue)
                                ? stringValue
                                : null,
                          });
                        }}
                      />
                    </InputField>
                  )}

                  {shouldShowPreferredCommunicationsMethod && (
                    <InputField>
                      <Label>
                        {
                          mutableContextFeatures.preferredCommunicationMethod
                            .name
                        }
                        *
                      </Label>
                      <ButtonSelector
                        required
                        options={preferredCommunicationMethod}
                        selected={sharedTaskState.preferredCommunicationMethod}
                        onChange={value =>
                          setSharedTaskState({
                            ...sharedTaskState,
                            preferredCommunicationMethod: value
                              ? value.toString()
                              : undefined,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.dropOffTime && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.dropOffTime.name}
                        {mutableContextFeatures.dropOffTime.required ? "*" : ""}
                      </Label>
                      <TimePicker
                        date={
                          getFromMetadataState("dropOffTime")
                            ? moment(
                                getFromMetadataState("dropOffTime"),
                                "H:mm",
                              )
                            : undefined
                        }
                        onChange={nextTime =>
                          setMetadataState({
                            ...metadataState,
                            dropOffTime: nextTime.format("H:mm"),
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.pickUpTime && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.pickUpTime.name}
                        {mutableContextFeatures.pickUpTime.required ? "*" : ""}
                      </Label>
                      <TimePicker
                        date={
                          getFromMetadataState("pickUpTime")
                            ? moment(getFromMetadataState("pickUpTime"), "H:mm")
                            : undefined
                        }
                        onChange={nextTime =>
                          setMetadataState({
                            ...metadataState,
                            pickUpTime: nextTime.format("H:mm"),
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.dropOffLocation && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.dropOffLocation.name}
                        {mutableContextFeatures.dropOffLocation.required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={dropOffLocations}
                        required={
                          mutableContextFeatures.dropOffLocation.required
                        }
                        selected={getFromMetadataState("dropOffLocation")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            dropOffLocation: value as FilterDropOffLocation,
                          })
                        }
                      />
                    </InputField>
                  )}

                  {mutableContextFeatures.guestUrgency && (
                    <InputField>
                      <Label>
                        {mutableContextFeatures.guestUrgency.name}
                        {mutableContextFeatures.guestUrgency.required
                          ? "*"
                          : ""}
                      </Label>
                      <ButtonSelector
                        options={guestUrgency}
                        required={mutableContextFeatures.guestUrgency.required}
                        selected={getFromMetadataState("guestUrgency")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            guestUrgency: value as GuestUrgency,
                          })
                        }
                      />
                    </InputField>
                  )}
                </StyledInputGroup>

                {mutableContextFeatures.sourceOfRelocationRequest && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.sourceOfRelocationRequest.name}
                      {mutableContextFeatures.sourceOfRelocationRequest.required
                        ? "*"
                        : ""}
                    </Label>
                    <RadioSelector
                      label="Source of Relocation"
                      options={sourceOfRelocationRequest}
                      required={
                        mutableContextFeatures.sourceOfRelocationRequest
                          .required
                      }
                      selected={metadataSourceOfRelocationRequest}
                      onChange={value =>
                        setMetadataState({
                          ...metadataState,
                          detailsOfReasonForRelocation: null,
                          reasonForRelocation: null,
                          sourceOfRelocationRequest: value
                            ? (value as SourceOfRelocationRequest)
                            : null,
                        })
                      }
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.reasonForRelocation && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.reasonForRelocation.name}
                      {mutableContextFeatures.reasonForRelocation.required
                        ? "*"
                        : ""}
                    </Label>
                    <RadioSelector
                      disabled={!metadataSourceOfRelocationRequest}
                      label="Reason for Relocation"
                      options={
                        metadataSourceOfRelocationRequest
                          ? reasonsForRelocation[
                              metadataSourceOfRelocationRequest as SourceOfRelocationRequest
                            ]
                          : []
                      }
                      required={
                        mutableContextFeatures.reasonForRelocation.required
                      }
                      selected={getFromMetadataState("reasonForRelocation")}
                      onChange={value =>
                        setMetadataState({
                          ...metadataState,
                          detailsOfReasonForRelocation: null,
                          reasonForRelocation: value
                            ? (value as ReasonForRelocation)
                            : null,
                        })
                      }
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.detailsOfReasonForRelocation && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.detailsOfReasonForRelocation.name}
                      {mutableContextFeatures.detailsOfReasonForRelocation
                        .required
                        ? "*"
                        : ""}
                    </Label>
                    <StyledEditableTextWrapper>
                      <EditableText
                        placeholder="Specify"
                        value={getFromMetadataState(
                          "detailsOfReasonForRelocation",
                        )}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            detailsOfReasonForRelocation: value,
                          })
                        }
                      />
                    </StyledEditableTextWrapper>
                  </BlockInputField>
                )}

                {mutableContextFeatures.preferredRelocationUnit && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.preferredRelocationUnit.name}
                      {mutableContextFeatures.preferredRelocationUnit.required
                        ? "*"
                        : ""}
                    </Label>
                    <TaskSubjectSearch
                      subject={formState.preferredRelocationUnit}
                      subjectType={
                        subjectTypesConfig[TaskContextSubjectTypeEnum.Unit]
                      }
                      onChange={subject => {
                        const subjectId = subject
                          ? getSearchItemId(subject)
                          : null;
                        if (
                          subjectId !==
                          getFromMetadataState("preferredRelocationUnit")
                        ) {
                          setMetadataState({
                            ...metadataState,
                            preferredRelocationUnit: subjectId,
                          });
                          setFormState({
                            ...formState,
                            preferredRelocationUnit: subject,
                          });
                        }
                      }}
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.fallbackRelocationUnit && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.fallbackRelocationUnit.name}
                      {mutableContextFeatures.fallbackRelocationUnit.required
                        ? "*"
                        : ""}
                    </Label>
                    <TaskSubjectSearch
                      subject={formState.fallbackRelocationUnit}
                      subjectType={
                        subjectTypesConfig[TaskContextSubjectTypeEnum.Unit]
                      }
                      onChange={subject => {
                        const subjectId = subject
                          ? getSearchItemId(subject)
                          : null;
                        if (
                          subjectId !==
                          getFromMetadataState("fallbackRelocationUnit")
                        ) {
                          setMetadataState({
                            ...metadataState,
                            fallbackRelocationUnit: subjectId,
                          });
                          setFormState({
                            ...formState,
                            fallbackRelocationUnit: subject,
                          });
                        }
                      }}
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.relocationIntendedTime && (
                  <BlockInputField>
                    <Label>Time of Relocation</Label>
                    <TaskDueByDate
                      isTurnoverDisabled
                      alignContent="left"
                      bookingInfo={null}
                      date={
                        getFromMetadataState("relocationIntendedTime")
                          ? moment(
                              getFromMetadataState("relocationIntendedTime"),
                            )
                          : null
                      }
                      eventName={EVENT_NAME}
                      futureCheckouts={[]}
                      recurrenceSetting={null}
                      renderTrigger={props => (
                        <CustomTaskDueByDateInlineTrigger
                          {...props}
                          title="Relocate by"
                        />
                      )}
                      taskId={TASK_ID}
                      onChange={variables =>
                        setMetadataState({
                          ...metadataState,
                          relocationIntendedTime: variables?.dueBy || null,
                        })
                      }
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.securityDepositAmount && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.securityDepositAmount.name}
                      {mutableContextFeatures.securityDepositAmount.required
                        ? "*"
                        : ""}
                    </Label>
                    <Input
                      placeholder="Enter an amount"
                      required={
                        mutableContextFeatures.securityDepositAmount.required
                      }
                      type="number"
                      value={
                        getFromMetadataState("securityDepositAmount") || ""
                      }
                      onChange={({ currentTarget: { value } }) => {
                        if (value)
                          setMetadataState({
                            ...metadataState,
                            securityDepositAmount: parseFloat(value),
                          });
                        else
                          setMetadataState(
                            omit(metadataState, [
                              "securityDepositAmount",
                            ]) as MetadataState,
                          );
                      }}
                    />
                  </BlockInputField>
                )}

                {mutableContextFeatures.securityDepositReason && (
                  <BlockInputField>
                    <Label>
                      {mutableContextFeatures.securityDepositReason.name}
                      {mutableContextFeatures.securityDepositReason.required
                        ? "*"
                        : ""}
                    </Label>
                    <StyledEditableTextWrapper>
                      <EditableText
                        placeholder="Enter a reason"
                        value={getFromMetadataState("securityDepositReason")}
                        onChange={value =>
                          setMetadataState({
                            ...metadataState,
                            securityDepositReason: value,
                          })
                        }
                      />
                    </StyledEditableTextWrapper>
                  </BlockInputField>
                )}

                {mutableContextFeatures.requestedCleaningDate &&
                  mutableContextFeatures.requestedCleaningTimeSlot && (
                    <>
                      <StyledInputGroup>
                        <InputField>
                          <Label>
                            {mutableContextFeatures.requestedCleaningDate.name}
                            {mutableContextFeatures.requestedCleaningDate
                              .required
                              ? "*"
                              : ""}
                          </Label>
                          <DateTimePickerContainer>
                            <Dropdown
                              trigger={
                                <Button iconRight>
                                  {getFromMetadataState("requestedCleaningDate")
                                    ? moment(
                                        getFromMetadataState(
                                          "requestedCleaningDate",
                                        ),
                                      ).format("ddd MMM D")
                                    : "Select a Date"}
                                  <ChevronDown />
                                </Button>
                              }
                            >
                              {({ isOpen, closePortal }) =>
                                isOpen && (
                                  <DropdownContent>
                                    <DateTimePicker
                                      date={
                                        getFromMetadataState(
                                          "requestedCleaningDate",
                                        )
                                          ? moment(
                                              getFromMetadataState(
                                                "requestedCleaningDate",
                                              ),
                                            )
                                          : null
                                      }
                                      isOutsideRange={day =>
                                        day
                                          ? day.isBefore(
                                              moment().startOf("day"),
                                            )
                                          : true
                                      }
                                      showTimePicker={false}
                                      onDateChange={nextDate => {
                                        if (nextDate)
                                          setMetadataState({
                                            ...metadataState,
                                            requestedCleaningDate: nextDate
                                              .clone()
                                              .startOf("day"),
                                          });

                                        closePortal();
                                      }}
                                    />
                                  </DropdownContent>
                                )
                              }
                            </Dropdown>
                          </DateTimePickerContainer>
                        </InputField>
                        <InputField>
                          <Label>
                            {
                              mutableContextFeatures.requestedCleaningTimeSlot
                                .name
                            }
                            {mutableContextFeatures.requestedCleaningTimeSlot
                              .required
                              ? "*"
                              : ""}
                          </Label>
                          <RadioSelector
                            disabled={
                              intrastayCleaningConfigAvailableTimes.length === 0
                            }
                            label="Select a Time Slot"
                            options={intrastayCleaningConfigAvailableTimes.map(
                              time => ({
                                value: time,
                                displayName:
                                  intrastayCleaningConfigTimeSlotDisplayNames[
                                    time as CleaningWindow
                                  ],
                              }),
                            )}
                            required={
                              mutableContextFeatures.requestedCleaningTimeSlot
                                .required
                            }
                            selected={getFromMetadataState(
                              "requestedCleaningTimeSlot",
                            )}
                            onChange={value =>
                              setMetadataState({
                                ...metadataState,
                                requestedCleaningTimeSlot:
                                  value as RequestedCleaningTimeSlot,
                              })
                            }
                          />
                        </InputField>
                      </StyledInputGroup>
                      {intrastayCleaningConfigAvailableTimes.length === 0 && (
                        <StyledValidationException>
                          <div>
                            Intrastay cleans are not available in this building
                            or this building is not yet configured.
                          </div>
                          <div>
                            Contact the city operations team for more help.
                          </div>
                        </StyledValidationException>
                      )}
                    </>
                  )}
              </>
            )}
            <BlockInputField>
              <Label>Notes</Label>
              <StyledEditableTextWrapper>
                <EditableText
                  value={sharedTaskState.notes}
                  onChange={notes =>
                    setSharedTaskState({
                      ...sharedTaskState,
                      notes,
                    })
                  }
                />
              </StyledEditableTextWrapper>
            </BlockInputField>
            <BlockInputField>
              <Label>Attachments</Label>
              {isBulkSubject ? (
                <>
                  <FileInputAttachments
                    files={bulkFiles}
                    onAdd={addedFiles => {
                      onAddFiles(addedFiles);
                    }}
                    onDelete={deletedFile => {
                      onDeleteFile(deletedFile);
                    }}
                  />
                  {bulkFiles.filter(f => f.error).length > 0 && (
                    <StyledValidationException>
                      <p>
                        Files that are marked Red were unable to be uploaded,
                        and will not be attached to the Task.
                      </p>
                    </StyledValidationException>
                  )}
                </>
              ) : (
                <FileInputAttachments
                  files={files}
                  onChange={newFiles => {
                    // Preserve Legacy Flow (Single Subject Creation)
                    // TODO: Remove this and use `bulkFiles` pattern for both flows.
                    setFiles(newFiles);
                  }}
                />
              )}
            </BlockInputField>
          </>
        )}
      </>
    ),
  };
  const AssignFields = {
    Title: "Assign",
    Error,
    Header: (
      <>
        <FullDivider />
      </>
    ),
    Content: (
      <>
        <StyledSectionHeader>Summary</StyledSectionHeader>
        <StyledPreviewComponent>
          {isSingleSubject && (
            <BlockInputField>
              <Label>{formState.subjectType.displayName}</Label>
              <div>
                {subjectPreviewText.title && (
                  <StyledPreviewTitle>
                    {subjectPreviewText.title}
                  </StyledPreviewTitle>
                )}
                {subjectPreviewText.subtitle && (
                  <span>{subjectPreviewText.subtitle}</span>
                )}
              </div>
            </BlockInputField>
          )}
          {isBulkSubject && (
            <>
              <BlockInputField>
                {formState.bulkFormState?.parentSubjectType?.displayName &&
                  bulkSubjectPreviewText && (
                    <>
                      <Label>
                        {formState.bulkFormState.parentSubjectType.displayName}
                      </Label>
                      <div>
                        {bulkSubjectPreviewText.title && (
                          <StyledPreviewTitle>
                            {bulkSubjectPreviewText.title}
                          </StyledPreviewTitle>
                        )}
                        {bulkSubjectPreviewText.subtitle && (
                          <span>{bulkSubjectPreviewText.subtitle}</span>
                        )}
                      </div>
                    </>
                  )}
              </BlockInputField>
              <BlockInputField>
                <Label>
                  {formState.subjectType.bulkTaskConfig?.displayName ||
                    "Subject(s)"}
                </Label>
                <TaskSubjectBulkSearchResults
                  subjects={formState.bulkFormState?.subjects || []}
                />
              </BlockInputField>
            </>
          )}
          <BlockInputField>
            <Label>Type</Label>
            <StyledPreviewTitle>
              {sharedTaskState.type
                ? getTaskOptionByType(sharedTaskState.type, false)?.displayName
                : "No Task Type"}
            </StyledPreviewTitle>
          </BlockInputField>
          <BlockInputField>
            <Label>Context</Label>
            <div>
              {taskContextState?.contextName ? (
                <StyledPreviewTitle>
                  {taskContextState?.contextName}
                </StyledPreviewTitle>
              ) : null}
              <span>
                {(formState.searchedContext &&
                  formState.searchedContext.subtitle) ||
                  (formState.guidedFlowState &&
                    [
                      formState.subjectType.displayName,
                      formState.guidedFlowState.location?.name,
                      formState.guidedFlowState.category?.name,
                    ]
                      .filter(x => Boolean(x))
                      .join(" > "))}
              </span>
            </div>
          </BlockInputField>
          {sharedTaskState.notes && (
            <BlockInputField>
              <Label>Notes</Label>
              <div>{sharedTaskState.notes}</div>
            </BlockInputField>
          )}
          {((isBulkSubject &&
            bulkFiles.filter(file => !file.error && !file.uploading).length >
              0) ||
            (isSingleSubject && files.length > 0)) && (
            <BlockInputField>
              <Label>Attachments</Label>
              <ImageGallery>
                {isBulkSubject &&
                  bulkFiles
                    .filter(file => !file.error && !file.uploading)
                    .map(file => (
                      <ImageSlide key={file.id}>
                        <Image src={file.url} />
                      </ImageSlide>
                    ))}
                {isSingleSubject &&
                  files.map(file => (
                    <ImageSlide key={file.id}>
                      <Image src={file.url} />
                    </ImageSlide>
                  ))}
              </ImageGallery>
            </BlockInputField>
          )}
        </StyledPreviewComponent>
        <StyledSectionHeader>Dispatch</StyledSectionHeader>
        {isBulkSubject && (formState.bulkFormState?.subjects || []).length > 0 && (
          <BulkSubjectCreationInfoPanel>
            <WarningIcon />
            <span>{`All ${
              (formState.bulkFormState?.subjects || []).length
            } task(s) will share the same Assignee, Priority, and Due At`}</span>
          </BulkSubjectCreationInfoPanel>
        )}
        <StyledInputGroup>
          {canAssign(sharedTaskState.type) && (
            <InputField>
              <Label>Assignee</Label>
              <AssigneePicker
                assigneeIds={
                  sharedTaskState.assigneeId ? [sharedTaskState.assigneeId] : []
                }
                eventName={EVENT_NAME}
                renderTrigger={({ assignees }) => (
                  <StyledAssigneeWrapper>
                    <MultiAvatar avatars={assignees} maxLength={3} />
                  </StyledAssigneeWrapper>
                )}
                taskCityId={cityId || undefined}
                taskId={TASK_ID}
                taskType={sharedTaskState.type}
                onAssign={nextValue => {
                  if (isArray(nextValue))
                    setSharedTaskState({
                      ...sharedTaskState,
                      assigneeId: nextValue[0] ? nextValue[0] : undefined,
                    });
                  else
                    setSharedTaskState({
                      ...sharedTaskState,
                      assigneeId: nextValue,
                    });
                }}
              />
            </InputField>
          )}
          <InputField>
            <Label>Priority</Label>
            <TaskPriority
              eventName={EVENT_NAME}
              priority={sharedTaskState.priority}
              taskId={TASK_ID}
              onSetPriority={newPriority =>
                setSharedTaskState({
                  ...sharedTaskState,
                  priority: newPriority,
                })
              }
            />
          </InputField>
          <InputField>
            <Label>Due At</Label>
            <TaskDueByDate
              isTurnoverDisabled
              alignContent="left"
              bookingInfo={null}
              date={sharedTaskState.dueAt}
              eventName={EVENT_NAME}
              futureCheckouts={[]}
              recurrenceSetting={null}
              renderTrigger={props => (
                <CustomTaskDueByDateInlineTrigger {...props} />
              )}
              taskId={TASK_ID}
              onChange={variables =>
                setSharedTaskState({
                  ...sharedTaskState,
                  dueAt: variables?.dueBy || null,
                  // Wipe recurrence if date is wiped.
                  recurrenceSettings: variables?.dueBy
                    ? sharedTaskState.recurrenceSettings
                    : null,
                })
              }
            />
          </InputField>
          {validRecurrenceSubjects.includes(
            formState.subjectType.subjectType as unknown as TaskSubjectType,
          ) && (
            <InputField>
              <Label>Recurrence Settings</Label>
              <StandaloneTaskRecurrenceDropdown
                alignContent="left"
                disabled={!sharedTaskState.dueAt}
                eventName={EVENT_NAME}
                recurrenceSetting={sharedTaskState.recurrenceSettings}
                taskId={TASK_ID}
                onChange={recurrenceSettings =>
                  setSharedTaskState({
                    ...sharedTaskState,
                    recurrenceSettings,
                  })
                }
              />
            </InputField>
          )}
        </StyledInputGroup>
      </>
    ),
  };

  const FormFields = formState.isDispatching ? AssignFields : CreateFields;

  if (isLoadingPrefilledSubject) FormFields.Content = <List />;

  return {
    createError,
    isCreateDisabled,
    isCreating,
    isSingleSubject,
    isBulkSubject,
    formState,
    onReset,
    onSubmit,
    setFormState,

    // Components:
    FormFields,
  };
};
